<template>
  <b-card no-body class="vital-signs-form">
    <div
      class="d-flex justify-content-between align-items-center px-2 py-1 border-bottom bg-light"
    >
      <b class="small-text">
        Vital signs
        <span class="text-muted font-weight-normal">(ไม่บังคับ)</span>
      </b>
    </div>
    <div class="p-2">
      <label class="vs-label">Blood pressure</label>
      <div
        v-for="(row, i) in rows"
        :key="`${idPrefix}-bp-${i}`"
        class="d-flex align-items-center mb-1"
      >
        <div class="input-group input-group-sm flex-grow-1">
          <input
            :id="`${idPrefix}-sys-${i}`"
            v-model.number="row.systolic"
            type="number"
            class="form-control"
            :class="bpInputClass(row, 'systolic')"
            :min="ranges.systolic[0]"
            :max="ranges.systolic[1]"
            :title="rangeTitle('systolic')"
            placeholder="Systolic (ตัวบน)"
            aria-label="Systolic"
            @input="emitChange"
          />
          <div class="input-group-append input-group-prepend">
            <span class="input-group-text">/</span>
          </div>
          <input
            :id="`${idPrefix}-dia-${i}`"
            v-model.number="row.diastolic"
            type="number"
            class="form-control"
            :class="bpInputClass(row, 'diastolic')"
            :min="ranges.diastolic[0]"
            :max="ranges.diastolic[1]"
            :title="rangeTitle('diastolic')"
            placeholder="Diastolic (ตัวล่าง)"
            aria-label="Diastolic"
            @input="emitChange"
          />
          <div class="input-group-append">
            <span class="input-group-text">mmHg</span>
          </div>
        </div>
        <span v-if="rows.length > 1" class="description text-muted ml-2"
          >#{{ i + 1 }}</span
        >
      </div>
      <div
        v-if="bpAlert"
        class="alert py-1 px-2 mb-2 small-text d-flex justify-content-between align-items-center"
        :class="bpAlert.cls"
      >
        <span>{{ bpAlert.text }}</span>
        <b-button
          size="sm"
          variant="outline-dark"
          class="py-0"
          @click="addRemeasure"
        >
          + วัดซ้ำ
        </b-button>
      </div>

      <b-form-row class="mb-1">
        <b-col cols="4">
          <label class="vs-label" :for="`${idPrefix}-pulse`">Pulse</label>
          <b-input-group size="sm" append="bpm">
            <b-form-input
              :id="`${idPrefix}-pulse`"
              v-model.number="first.pulse"
              :min="ranges.pulse[0]"
              :max="ranges.pulse[1]"
              :state="fieldState('pulse')"
              :title="rangeTitle('pulse')"
              type="number"
              @input="emitChange"
            />
          </b-input-group>
        </b-col>
        <b-col cols="4">
          <label class="vs-label" :for="`${idPrefix}-temp`">Temp</label>
          <b-input-group size="sm" append="°C">
            <b-form-input
              :id="`${idPrefix}-temp`"
              v-model.number="first.tempC"
              :min="ranges.tempC[0]"
              :max="ranges.tempC[1]"
              :state="fieldState('tempC')"
              :title="rangeTitle('tempC')"
              type="number"
              step="0.1"
              @input="emitChange"
            />
          </b-input-group>
        </b-col>
        <b-col cols="4">
          <label class="vs-label" :for="`${idPrefix}-spo2`">SpO₂</label>
          <b-input-group size="sm" append="%">
            <b-form-input
              :id="`${idPrefix}-spo2`"
              v-model.number="first.spo2"
              :min="ranges.spo2[0]"
              :max="ranges.spo2[1]"
              :state="fieldState('spo2')"
              :title="rangeTitle('spo2')"
              type="number"
              @input="emitChange"
            />
          </b-input-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col cols="4">
          <label class="vs-label" :for="`${idPrefix}-weight`">น้ำหนัก</label>
          <b-input-group size="sm" append="kg">
            <b-form-input
              :id="`${idPrefix}-weight`"
              v-model.number="first.weightKg"
              :min="ranges.weightKg[0]"
              :max="ranges.weightKg[1]"
              :state="fieldState('weightKg')"
              :title="rangeTitle('weightKg')"
              type="number"
              step="0.1"
              :placeholder="lastWeightText"
              @input="emitChange"
            />
          </b-input-group>
          <div
            v-if="lastMeasurements && lastMeasurements.weightKg"
            class="description text-muted"
          >
            ล่าสุด {{ formatDate(lastMeasurements.weightDt) }}
          </div>
        </b-col>
        <b-col cols="4">
          <label class="vs-label" :for="`${idPrefix}-height`">ส่วนสูง</label>
          <b-input-group size="sm" append="cm">
            <b-form-input
              :id="`${idPrefix}-height`"
              v-model.number="first.heightCm"
              :min="ranges.heightCm[0]"
              :max="ranges.heightCm[1]"
              :state="fieldState('heightCm')"
              :title="rangeTitle('heightCm')"
              type="number"
              step="0.1"
              :placeholder="lastHeightText"
              @input="emitChange"
            />
          </b-input-group>
          <div
            v-if="lastMeasurements && lastMeasurements.heightCm"
            class="description text-muted"
          >
            ล่าสุด {{ formatDate(lastMeasurements.heightDt) }}
          </div>
        </b-col>
        <b-col cols="4">
          <label class="vs-label">BMI</label>
          <div
            class="form-control form-control-sm bg-light d-flex align-items-center"
          >
            <span>{{ bmi === null ? "-" : bmi }}</span>
            <b-badge
              v-if="bmiCat"
              class="ml-1"
              :variant="bmiCat.key === 'normal' ? 'success' : 'warning'"
            >
              {{ bmiCat.label }}
            </b-badge>
          </div>
        </b-col>
      </b-form-row>
      <b-link v-if="canUseLast" class="description" @click="useLastMeasurements"
        >ใช้ค่าล่าสุด</b-link
      >
    </div>
  </b-card>
</template>

<script>
import moment from "moment";
import {
  emptyReading,
  computeBmi,
  bmiCategory,
  bpLevel,
  isBlank,
  isOutOfRange,
  rangeMessage,
  RANGES,
} from "@/utils/vitalSigns";

export default {
  name: "VitalSignsForm",
  props: {
    value: { type: Array, default: () => [] },
    lastMeasurements: { type: Object, default: null },
    idPrefix: { type: String, default: "vs" },
  },
  data() {
    return { rows: [emptyReading()], ranges: RANGES };
  },
  computed: {
    first() {
      return this.rows[0];
    },
    bmi() {
      return computeBmi(this.first.weightKg, this.first.heightCm);
    },
    bmiCat() {
      return bmiCategory(this.bmi);
    },
    latestBpLevel() {
      for (let i = this.rows.length - 1; i >= 0; i--) {
        const level = bpLevel(this.rows[i].systolic, this.rows[i].diastolic);
        if (level) return level;
      }
      return null;
    },
    bpAlert() {
      if (this.latestBpLevel === "danger") {
        return {
          cls: "alert-danger",
          text: "ความดันสูงมาก (≥180/110) แนะนำให้วัดซ้ำ",
        };
      }
      if (this.latestBpLevel === "warning") {
        return { cls: "alert-warning", text: "ความดันสูงกว่าเกณฑ์ (≥140/90)" };
      }
      return null;
    },
    lastWeightText() {
      const lm = this.lastMeasurements;
      return lm && lm.weightKg ? `ล่าสุด ${lm.weightKg}` : "";
    },
    lastHeightText() {
      const lm = this.lastMeasurements;
      return lm && lm.heightCm ? `ล่าสุด ${lm.heightCm}` : "";
    },
    canUseLast() {
      const lm = this.lastMeasurements;
      if (!lm) return false;
      return Boolean(
        (lm.weightKg && isBlank(this.first.weightKg)) ||
          (lm.heightCm && isBlank(this.first.heightCm))
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        const next = v && v.length ? v : [emptyReading()];
        if (JSON.stringify(next) === JSON.stringify(this.rows)) return;
        this.rows = next.map(r => ({ ...emptyReading(), ...r }));
      },
    },
  },
  methods: {
    emitChange() {
      this.$emit(
        "input",
        this.rows.map(r => ({ ...r }))
      );
    },
    addRemeasure() {
      this.rows.push(emptyReading());
      this.emitChange();
    },
    useLastMeasurements() {
      const lm = this.lastMeasurements;
      if (lm.weightKg && isBlank(this.first.weightKg)) {
        this.first.weightKg = lm.weightKg;
      }
      if (lm.heightCm && isBlank(this.first.heightCm)) {
        this.first.heightCm = lm.heightCm;
      }
      this.emitChange();
    },
    bpInputClass(row, field) {
      const invalid = isOutOfRange(field, row[field]);
      const level = bpLevel(row.systolic, row.diastolic);
      return {
        "is-invalid": invalid,
        "vs-bp-warning": !invalid && level === "warning",
        "vs-bp-danger": !invalid && level === "danger",
      };
    },
    // false shows bootstrap's red outline; null leaves the input unstyled.
    fieldState(field) {
      return isOutOfRange(field, this.first[field]) ? false : null;
    },
    rangeTitle(field) {
      return rangeMessage(field);
    },
    formatDate(dt) {
      return dt
        ? moment(dt)
            .locale("th")
            .format("D MMM YY")
        : "";
    },
  },
};
</script>

<style scoped>
.vs-label {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 2px;
}
.vs-bp-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
}
.vs-bp-danger {
  background-color: #f8d7da;
  border-color: #dc3545;
}
</style>
